/**
 * Collections API - Guest Access Links
 *
 * Create, update, revoke, and verify guest access links.
 */

import { z } from 'zod'
import { CollectionSummarySchema, GuestAccessLinkSchema } from './common'

// ============================================================================
// GET /v1/collections/:id/guest-access
// ============================================================================

export const ListGuestAccessLinksResponseSchema = z.object({
  data: z.array(GuestAccessLinkSchema),
  message: z.string()
})

export type ListGuestAccessLinksResponse = z.infer<typeof ListGuestAccessLinksResponseSchema>

// ============================================================================
// POST /v1/collections/:id/guest-access
// ============================================================================

export const CreateGuestAccessLinkRequestSchema = z.object({
  password: z.string().min(1).max(100).optional().nullable(),
  expiresAt: z.string().datetime().optional().nullable(),
  allowDownload: z.boolean().optional().default(false),
  showMetadata: z.boolean().optional().default(true)
})

export type CreateGuestAccessLinkRequest = z.infer<typeof CreateGuestAccessLinkRequestSchema>

export const CreateGuestAccessLinkResponseSchema = z.object({
  data: GuestAccessLinkSchema.extend({
    url: z.string().describe('Full guest access URL')
  }),
  message: z.string()
})

export type CreateGuestAccessLinkResponse = z.infer<typeof CreateGuestAccessLinkResponseSchema>

// ============================================================================
// PATCH /v1/collections/:collectionId/guest-access/:linkId
// ============================================================================

export const UpdateGuestAccessLinkRequestSchema = z.object({
  password: z.string().min(1).max(100).optional().nullable(),
  expiresAt: z.string().datetime().optional().nullable(),
  allowDownload: z.boolean().optional(),
  showMetadata: z.boolean().optional(),
  isActive: z.boolean().optional()
})

export type UpdateGuestAccessLinkRequest = z.infer<typeof UpdateGuestAccessLinkRequestSchema>

export const UpdateGuestAccessLinkResponseSchema = z.object({
  data: GuestAccessLinkSchema,
  message: z.string()
})

export type UpdateGuestAccessLinkResponse = z.infer<typeof UpdateGuestAccessLinkResponseSchema>

// ============================================================================
// DELETE /v1/collections/:collectionId/guest-access/:linkId
// ============================================================================

export const RevokeGuestAccessLinkResponseSchema = z.object({
  data: z.object({ success: z.boolean() }),
  message: z.string()
})

export type RevokeGuestAccessLinkResponse = z.infer<typeof RevokeGuestAccessLinkResponseSchema>

// ============================================================================
// POST /v1/collections/guest-access/:token/verify
// ============================================================================

export const VerifyGuestAccessRequestSchema = z.object({
  token: z.string(),
  password: z.string().optional()
})

export type VerifyGuestAccessRequest = z.infer<typeof VerifyGuestAccessRequestSchema>

export const VerifyGuestAccessResponseSchema = z.object({
  data: z.object({
    valid: z.boolean(),
    collection: CollectionSummarySchema.optional(),
    allowDownload: z.boolean().optional(),
    showMetadata: z.boolean().optional()
  }),
  message: z.string()
})

export type VerifyGuestAccessResponse = z.infer<typeof VerifyGuestAccessResponseSchema>
